import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

interface StartSellerRonSessionResponse {
	whitelabelToken: string;
	vendorOrderId?: string;
	purchasedVehicleIds: string[];
	progress: unknown;
}

/**
 * API function to start a seller RON session for counter-signing.
 * Returns a whitelabel token for embedding the NotaryLive iframe.
 */
export const startSellerRonSession = async (
	servicesProviderId: string,
	orderId: string,
): Promise<StartSellerRonSessionResponse> => {
	const response = await apiClient.post<StartSellerRonSessionResponse>(
		ordersEndpoints.startSellerRonSession(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for starting a seller RON session.
 *
 * @param orderId - The purchased vehicle ID
 */
export const useStartSellerRonSession = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!orderId) {
				throw new Error('Unable to start seller RON session without an order ID.');
			}
			if (!servicesProviderId) {
				throw new Error(
					'Unable to start seller RON session without a services provider ID.',
				);
			}
			return startSellerRonSession(servicesProviderId, orderId);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;

			// Optimistically update the progress query with the returned progress
			if (data.progress) {
				queryClient.setQueryData(
					queries.orders.detail(servicesProviderId, orderId)._ctx.progress.queryKey,
					data.progress,
				);
			}
		},
	});
};
